import classnames from 'classnames';
import { type ComponentRef, useEffect, useRef, useState } from 'react';
import '../../../../css/user.css';
import { ExIcon, IconVariant } from '@boomi/exosphere';
import { useDocumentClick } from '../../hooks/document';
import translations from '../../translations';
import type { UserTenantResponseAPI } from '../../types/user';
import { getDateDisplay } from '../../utils/date';
import { isNullOrEmpty } from '../../utils/guard';
import { formatTenantName } from '../../utils/tenant';
import CopyableText from '../generic/CopyableText';
import TenantName from '../generic/TenantName';
import { useIsSingleTenant } from '../../hooks/organization';

interface TenantDropdown {
    tenant: UserTenantResponseAPI;
    openTenantSelector: () => void;
}

const TenantDropdown = ({ tenant, openTenantSelector }: TenantDropdown) => {
    const { id, isSso, developerName, organization, expiresAt } = tenant;
    const isSingleTenant = useIsSingleTenant(tenant);

    const [isOpen, setIsOpen] = useState(false);

    const changeTenantButtonRef = useRef<ComponentRef<'button'>>(null);
    const dropDownRef = useRef<ComponentRef<'div'>>(null);

    useDocumentClick(dropDownRef, () => setIsOpen(false));

    useEffect(() => {
        if (!isOpen || isSso) {
            return;
        }

        changeTenantButtonRef.current?.focus();
    }, [isOpen, isSso]);

    const handleChangeTenantClick = () => {
        setIsOpen(false);
        openTenantSelector();
    };

    const classes = classnames('dropdown-box', {
        'is-open': isOpen,
    });

    return (
        <div ref={dropDownRef} className={classes}>
            <button
                className="dropdown-btn"
                onClick={() => setIsOpen(!isOpen)}
                data-testid="user-dropdown-button"
                type="button"
            >
                <div className="header-dropdown">
                    <span className="user-dropdown-text">
                        <TenantName name={developerName} />
                    </span>
                    <ExIcon
                        className="icon-small dropdown-btn-icon"
                        icon="Down caret"
                        label="Expand"
                        variant={IconVariant.INVERSE}
                    />
                    {isSso && <span className="label label-info">SSO</span>}
                </div>
            </button>
            <div className="dropdown-list-wrapper">
                <div className="user-dropdown-content" data-testid="user-dropdown-content">
                    <CopyableText copyableText={formatTenantName(developerName)} />
                    {organization && (
                        <p className="list-group-item-text margin-top-small">{organization.name}</p>
                    )}
                    <div className="user-tenant-id quiet">
                        <CopyableText copyableText={id} hasCopyButton={true} />
                    </div>
                    {!isNullOrEmpty(expiresAt) && (
                        <p className="list-group-item-text quiet margin-top-small">
                            {`Tenant expires: ${getDateDisplay(expiresAt)}`}
                        </p>
                    )}
                    {!isSso && !isSingleTenant && (
                        <>
                            <hr />
                            <ul className="list-unstyled">
                                <li>
                                    <button
                                        className="btn btn-info"
                                        ref={changeTenantButtonRef}
                                        onClick={handleChangeTenantClick}
                                        type="button"
                                    >
                                        {translations.HEADER_change_tenant}
                                    </button>
                                </li>
                            </ul>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TenantDropdown;
